import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

/** One step of a trail: a page above this one, or this one. */
export interface Crumb {
  /** What the step is called — a record's name, or null while the record is on its way. */
  label: string | null
  /** Where the step leads. The last one is the page itself, and leads nowhere. */
  to?: string
}

/**
 * Where this page sits — *Carriers › Trail Lines › Truck 12* (TRKC-97: Hugo asked for a way
 * to move around the site, and some pages had a "back" link while others did not). Every
 * detail and form page carries one, so the way back up never depends on which pages grew a
 * link of their own. The APG breadcrumb: a labelled navigation landmark holding an ordered
 * list, the last step marked as the current page, and separators that are drawn, never read.
 */
export function Breadcrumbs({ trail }: { trail: readonly Crumb[] }) {
  const { t } = useTranslation()

  return (
    <nav className="breadcrumbs" aria-label={t('breadcrumbs.label')}>
      <ol className="breadcrumbs__list">
        {trail.map((crumb, index) => {
          const current = index === trail.length - 1
          const label = crumb.label ?? t('breadcrumbs.loading')

          return (
            // A trail is short and fixed per page: a step's position is its identity.
            <li key={index} className="breadcrumbs__step">
              {index > 0 && (
                <span className="breadcrumbs__separator" aria-hidden="true">
                  ›
                </span>
              )}
              {current || crumb.to === undefined ? (
                <span aria-current={current ? 'page' : undefined}>{label}</span>
              ) : (
                <Link to={crumb.to}>{label}</Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
